import { useEffect, useRef } from 'react';
import type { FindMatchCardSymbol, FindMatchView } from '@minigame-race/contracts';
import { color } from '../theme.js';

/**
 * Find Match (Dobble) — two cards share exactly one symbol. Tap it first to win
 * the round; a wrong tap loses it. Best of 3.
 *
 * Ping-fair like Reaction Duel: we measure `findMs` LOCALLY — from the frame the
 * round's cards actually render (`shownAt`, a rAF-confirmed timestamp) to the tap
 * — so network latency cancels out of the local delta. The card container is
 * keyed on `round` so a fresh pair animates in instantly (no reveal pause).
 */
export function FindMatchBoard({
  view,
  onMove,
}: {
  view: FindMatchView;
  onMove: (move: unknown) => void;
}) {
  const { phase, round, totalRounds, wins, yourSeat, youAnswered, opponentAnswered, cardA, cardB, over, yourPick, yourPickCorrect, matchId } = view;
  const youWins = wins[yourSeat];
  const rivalWins = wins[yourSeat === 0 ? 1 : 0];
  const revealing = phase === 'reveal';

  // The instant THIS client painted the current round's cards. rAF-confirmed so
  // it reflects the real rendered frame. Reset each round.
  const shownAt = useRef<number | null>(null);
  useEffect(() => {
    shownAt.current = null;
    const id = requestAnimationFrame(() => { shownAt.current = performance.now(); });
    return () => cancelAnimationFrame(id);
  }, [round]);

  const answer = (symbolId: number): void => {
    if (youAnswered || over || revealing) return;
    const start = shownAt.current;
    const ms = start === null ? 0 : Math.max(0, Math.round(performance.now() - start));
    onMove({ type: 'tap', symbolId, findMs: ms });
  };

  return (
    <div style={{ width: 'min(88cqw, 340px)', height: '100cqh', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'min(1.6cqh,12px)', minHeight: 0 }}>
      {/* scoreboard */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Score label="YOU" value={youWins} tint={color.lime} />
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 10, letterSpacing: '.24em', color: 'rgba(234,255,234,.4)' }}>ROUND</div>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 16, fontWeight: 800 }}>
            {Math.min(round + 1, totalRounds)}/{totalRounds}
          </div>
        </div>
        <Score label="RIVAL" value={rivalWins} tint={color.cyan} align="right" />
      </div>

      {/* the two cards — flex-fill the remaining height, each card sized by height
          so the whole stack always fits the slot (no vertical overflow). */}
      <div key={round} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 'min(1.6cqh,12px)', animation: 'fmSwap .28s ease-out' }}>
        <Card symbols={cardA} onPick={answer} disabled={youAnswered || over || revealing} reveal={revealing ? { pick: yourPick, correct: yourPickCorrect, matchId } : null} />
        <Card symbols={cardB} onPick={answer} disabled={youAnswered || over || revealing} reveal={revealing ? { pick: yourPick, correct: yourPickCorrect, matchId } : null} />
      </div>

      <div style={{ textAlign: 'center', fontSize: 12, fontWeight: revealing ? 700 : 400, flexShrink: 0, color: revealing ? (yourPickCorrect ? color.lime : color.redBright) : 'rgba(234,255,234,.45)', minHeight: 16 }}>
        {over
          ? 'Match complete.'
          : revealing
            ? yourPick === null
              ? 'Rival got there first.'
              : yourPickCorrect
                ? 'Correct!'
                : 'Wrong symbol!'
            : youAnswered
              ? 'Answered — waiting…'
              : opponentAnswered
                ? 'Rival answered! Find the match fast!'
                : 'Tap the ONE symbol both cards share.'}
      </div>
    </div>
  );
}

function Card({
  symbols,
  onPick,
  disabled,
  reveal,
}: {
  symbols: readonly FindMatchCardSymbol[];
  onPick: (id: number) => void;
  disabled: boolean;
  reveal: { pick: number | null; correct: boolean | null; matchId: number | null } | null;
}) {
  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', justifyContent: 'center' }}>
    <div
      style={{
        position: 'relative',
        // Fill the available height; width follows the 4:3 aspect but never
        // exceeds the container. This makes the two-card stack fit ANY slot
        // height without overflow, on phones and desktop alike.
        height: '100%',
        maxWidth: '100%',
        aspectRatio: '4 / 3',
        borderRadius: 16,
        border: '2px solid rgba(255,120,200,.35)',
        background: 'radial-gradient(120% 140% at 50% 20%, rgba(40,20,34,.9), rgba(14,10,14,.95))',
        overflow: 'hidden',
      }}
    >
      {symbols.map((s) => {
        // Each symbol lives in a fixed-position CIRCLE; only its size varies.
        // Max diameter kept under the closest slot spacing (24.7% of card width)
        // so circles never overlap. scale ∈ [0.85,1.2] → diam ∈ [~17%,~22%].
        const diam = 14 + s.scale * 6.5; // % of card width
        // During reveal: mark the symbol YOU tapped with ✓/✗, and ring the true match.
        const isYourPick = reveal !== null && reveal.pick === s.id;
        const isTheMatch = reveal !== null && reveal.matchId === s.id;
        const badge = isYourPick ? (reveal!.correct ? '✓' : '✗') : null;
        const ring = isYourPick
          ? reveal!.correct
            ? color.lime
            : color.redBright
          : isTheMatch
            ? color.lime
            : null;
        return (
          <button
            key={s.id}
            type="button"
            disabled={disabled}
            onClick={() => onPick(s.id)}
            aria-label="symbol"
            style={{
              position: 'absolute',
              left: `${s.x * 100}%`,
              top: `${s.y * 100}%`,
              width: `${diam}%`,
              transform: 'translate(-50%,-50%)',
              aspectRatio: '1 / 1',
              borderRadius: '50%',
              border: ring ? `2px solid ${ring}` : '1px solid rgba(255,255,255,.08)',
              background: 'rgba(255,255,255,.05)',
              boxShadow: ring ? `0 0 10px ${ring}88` : undefined,
              display: 'grid',
              placeItems: 'center',
              padding: 0,
              cursor: disabled ? 'default' : 'pointer',
              userSelect: 'none',
              touchAction: 'manipulation',
            }}
          >
            <span style={{ fontSize: 'min(6cqmin,26px)', lineHeight: 1, transform: `rotate(${s.rot}deg)`, opacity: badge ? 0.35 : 1 }}>
              {GLYPHS[s.id % GLYPHS.length]}
            </span>
            {badge && (
              <span
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 'min(8cqw,28px)',
                  fontWeight: 900,
                  color: reveal!.correct ? color.lime : color.redBright,
                  textShadow: '0 1px 4px rgba(0,0,0,.8)',
                }}
              >
                {badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
    </div>
  );
}

function Score({ label, value, tint, align }: { label: string; value: number; tint: string; align?: 'right' }) {
  return (
    <div style={{ textAlign: align === 'right' ? 'right' : 'left' }}>
      <div style={{ fontSize: 10, letterSpacing: '.2em', color: 'rgba(234,255,234,.4)' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: tint, lineHeight: 1 }}>{value}</div>
    </div>
  );
}

/**
 * The symbol alphabet: at least as many entries as the deck has symbols (57 for
 * order-7). Emoji keep it zero-asset and instantly recognizable; the user will
 * swap for supplied art later.
 */
const GLYPHS = [
  '🍎', '🍌', '🍒', '🍇', '🍉', '🍓', '🍑', '🥝', '🍍', '🥥',
  '☀️', '🌙', '⭐', '⚡', '🔥', '❄️', '🌈', '☂️', '🍀', '🌵',
  '🐶', '🐱', '🦊', '🐼', '🐸', '🐵', '🦁', '🐯', '🐨', '🐷',
  '🚗', '✈️', '🚀', '⛵', '🚲', '🏀', '⚽', '🎾', '🎸', '🎺',
  '💎', '🔔', '🎁', '🔑', '🧩', '🎯', '🧭', '⏰', '💡', '🔮',
  '👑', '🎩', '🕶️', '🌷', '🍄', '🐢', '🦋',
];
